import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { LayoutService } from '@app/core/services/layout.service';
import { RememberService } from '@app/core/services/remember.service';
import { UtilityService } from '@app/core/services/utility.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { P2WebpageItem, P2WebpageSettingsGet, WebPageService } from '@app/services/web-page.service';

@Component({
    selector: 'tt-p2-webpage',
    templateUrl: './p2-webpage.component.html',
    styleUrls: ['./p2-webpage.component.css'],
    standalone: false,
})
export class P2WebpageComponent implements OnInit {
    public setting?: P2WebpageSettingsGet;
    public pages: P2WebpageItem[] = [];
    public itemState = '';
    public selectedPageKeyno = '';
    public readonly tabletSize: MediaQueryList;

    public readonly searchTextButtons: FormButton[] = [{
        id: 'erase',
        icon: 'fas fa-eraser',
        type: 'danger',
        onClick: () => this.clearSearchText(),
    }];

    private navigator?: TTViewNavigator;
    private readonly rememberId = 'p2Webpage.config';

    constructor(
        private webPage: WebPageService,
        private remember: RememberService,
        private utility: UtilityService,
        layout: LayoutService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) navigator?: TTViewNavigator,
    ) {
        this.tabletSize = layout.getMediaQueries().tablet;
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async loadPages(): Promise<void> {
        this.pages = await this.webPage.listP2Webpages();
        this.itemState = this.pages.find((item) => !!item.item_state)?.item_state ?? '';
    }

    public async updateList(): Promise<void> {
        this.selectedPageKeyno = '';
        await this.loadPages();
    }

    public openPage(item: P2WebpageItem, event?: MouseEvent): void {
        const keyno = `${item.item_id}`;
        if (this.tabletSize.matches && this.itemState === 'p2webpageedit') {
            this.selectedPageKeyno = keyno;
            return;
        }

        this.navigateToEdit(keyno, event);
    }

    public createPage(event?: MouseEvent): void {
        if (this.tabletSize.matches && this.itemState === 'p2webpageedit') {
            this.selectedPageKeyno = '0';
            return;
        }

        this.navigateToEdit('0', event);
    }

    public onSearchTextBlur(): void {
        if (this.setting?.searchtext) void this.rememberSearchText();
    }

    public clearSearchText(): void {
        if (!this.setting) return;

        this.setting.searchtext = '';
        void this.rememberSearchText();
    }

    public ngOnInit(): void {
        void this.init();
    }

    private async init(): Promise<void> {
        const [setting, lastStatus] = await Promise.all([
            this.webPage.getP2WebpageSettings(),
            this.remember.getLastStatus(this.rememberId, true),
            this.loadPages(),
        ]);
        this.setting = {
            ...setting,
            searchtext: typeof lastStatus?.searchText === 'string' ? lastStatus.searchText : setting.searchtext ?? '',
        };
    }

    private async rememberSearchText(): Promise<void> {
        await this.remember.remember(this.rememberId, JSON.stringify({ searchText: this.setting?.searchtext ?? '' }));
    }

    private navigateToEdit(keyno: string, event?: MouseEvent): void {
        const target = { to: 'p2webpageedit-test', parms: { p2_webpage_keyno: keyno } };
        if (event && this.navigator && this.utility.isModifierKeyPressed(event)) {
            void this.navigator.newTab(target);
        } else {
            void this.navigator?.go(target);
        }
    }
}
